import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Flag,
  GripVertical,
  Hand,
  Kanban,
  Loader2,
  Mail,
  MessageSquarePlus,
  Search,
  StretchHorizontal,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import TaskScoreModal, { type TaskScoreModalIntent } from './TaskScoreModal';
import TasksTimelineBoard, {
  readStoredTimelineRange,
  writeStoredTimelineRange,
  type TasksTimelineBoardHandle,
  type TimelineRange,
} from './TasksTimelineBoard';
import {
  isHighValueStrategyScore,
  isManualTaskSource,
  isOpenTaskStatus,
  isTaskOverdue,
  kanbanDropStatus,
  STRATEGY_SCORE_LABELS,
  TASK_KANBAN_COLUMNS,
  taskHasCompletionJustification,
  taskStatusUpdatePatch,
  taskTypeLabel,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
  type TaskKanbanColumnId,
  type WorkEpicRow,
} from '../../lib/networkingTasks';

type KindFilter = 'all' | 'transcript' | 'networking';
type BoardMode = 'columns' | 'timeline';

const BOARD_MODE_STORAGE_KEY = 'lccto.tasks.boardMode';
const BOARD_SEARCH_STORAGE_KEY = 'lccto.tasks.boardSearch';
const BOARD_EPIC_STORAGE_KEY = 'lccto.tasks.boardEpic';
const BOARD_CLOSED_STORAGE_KEY = 'lccto.tasks.boardShowClosed';

function readStoredBoardMode(): BoardMode {
  try {
    const v = localStorage.getItem(BOARD_MODE_STORAGE_KEY);
    if (v === 'timeline' || v === 'columns') return v;
  } catch {
    /* private mode / blocked */
  }
  return 'columns';
}

function writeStoredBoardMode(mode: BoardMode) {
  try {
    localStorage.setItem(BOARD_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

function readStoredSearch(): string {
  try {
    return localStorage.getItem(BOARD_SEARCH_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeStoredSearch(query: string) {
  try {
    const trimmed = query.trim();
    if (trimmed) localStorage.setItem(BOARD_SEARCH_STORAGE_KEY, query);
    else localStorage.removeItem(BOARD_SEARCH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readStoredEpic(): string {
  try {
    return localStorage.getItem(BOARD_EPIC_STORAGE_KEY) || 'all';
  } catch {
    return 'all';
  }
}

function writeStoredEpic(epicId: string) {
  try {
    if (epicId && epicId !== 'all') localStorage.setItem(BOARD_EPIC_STORAGE_KEY, epicId);
    else localStorage.removeItem(BOARD_EPIC_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readStoredShowClosed(): boolean {
  try {
    return localStorage.getItem(BOARD_CLOSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredShowClosed(show: boolean) {
  try {
    if (show) localStorage.setItem(BOARD_CLOSED_STORAGE_KEY, '1');
    else localStorage.removeItem(BOARD_CLOSED_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function formatDue(dueAt: string): string {
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return dueAt;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Eval board: person name only — never email. */
function contactLabel(task: NetworkingTaskRow): string | null {
  const name = task.subscriber?.name?.trim();
  return name || null;
}

function columnForTask(task: NetworkingTaskRow): TaskKanbanColumnId {
  if (task.status === 'skipped' || task.status === 'canceled') return 'archived';
  if (task.status === 'done') return 'done';
  // Eval board: open work buckets by score so alignment stays visible.
  if (isOpenTaskStatus(task.status)) {
    if (isHighValueStrategyScore(task.strategy_score)) return 'high_value';
    if (task.strategy_score == null) return 'needs_score';
    return 'low_pull';
  }
  return 'needs_score';
}

function scoreValue(task: NetworkingTaskRow): number {
  const n = Number(task.strategy_score);
  return Number.isFinite(n) ? n : 0;
}

function dueTime(task: NetworkingTaskRow): number {
  const t = new Date(task.due_at).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/** Within a score band: soonest due first; undated last. */
function sortByDueSoonest(a: NetworkingTaskRow, b: NetworkingTaskRow): number {
  const dueDiff = dueTime(a) - dueTime(b);
  if (dueDiff !== 0) return dueDiff;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

/** Highest strategy_score first, then soonest due. */
function sortByValueThenDue(a: NetworkingTaskRow, b: NetworkingTaskRow): number {
  const diff = scoreValue(b) - scoreValue(a);
  if (diff !== 0) return diff;
  return sortByDueSoonest(a, b);
}

interface TaskKanbanCardProps {
  task: NetworkingTaskRow;
  onOpenChat: (task: NetworkingTaskRow) => void;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
}

const TaskKanbanCard: React.FC<TaskKanbanCardProps> = ({
  task,
  onOpenChat,
  onDragStart,
  onDragEnd,
}) => {
  const overdue = isTaskOverdue(task.due_at, task.status);
  const subscriberHref = task.subscriber_id ? `/subscribers/${task.subscriber_id}?tab=tasks` : null;
  const transcriptHref = task.transcript_id ? `/transcripts/${task.transcript_id}` : null;
  const showHand = isManualTaskSource(task.source);
  const scoreLabel =
    typeof task.strategy_score === 'number'
      ? STRATEGY_SCORE_LABELS[task.strategy_score] ?? 'Score'
      : 'Unscored';

  return (
    <article
      role="button"
      tabIndex={0}
      draggable
      onDragStart={() => onDragStart(task.id)}
      onDragEnd={onDragEnd}
      onClick={() => onOpenChat(task)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenChat(task);
        }
      }}
      title="Open task chat"
      className={`group relative rounded-lg border bg-white dark:bg-gray-800 shadow-sm transition-shadow hover:shadow-md cursor-pointer ${
        overdue
          ? 'border-amber-300 dark:border-amber-700 ring-1 ring-amber-200/60 dark:ring-amber-800/40'
          : 'border-gray-200 dark:border-gray-600'
      }`}
    >
      <span
        title={scoreLabel}
        className={`absolute top-2 right-2 z-10 inline-flex items-center gap-0.5 min-w-[1.75rem] justify-center px-1.5 py-0.5 rounded-md text-xs font-bold tabular-nums pointer-events-none ${
          typeof task.strategy_score === 'number' && isHighValueStrategyScore(task.strategy_score)
            ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-100'
            : typeof task.strategy_score === 'number'
              ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
              : 'bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-500 border border-dashed border-gray-300 dark:border-gray-600'
        }`}
      >
        {typeof task.strategy_score === 'number' ? task.strategy_score : '—'}
        {showHand ? <Hand className="w-3 h-3 opacity-80" aria-hidden /> : null}
      </span>

      <div className="flex items-start gap-1 p-3 pr-10">
        <span
          className="mt-0.5 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing"
          title="Drag to another column"
          aria-hidden
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white leading-snug">
            {task.title || taskTypeLabel(task.task_type)}
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap items-center gap-1.5">
            <span>{taskTypeLabel(task.task_type)}</span>
            {task.epic?.title ? (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
                {task.epic.title}
                {task.epic_placement && task.epic_placement !== 'locked'
                  ? ` · ${task.epic_placement}`
                  : ''}
              </span>
            ) : null}
          </p>

          {transcriptHref ? (
            <Link
              to={transcriptHref}
              title="Open transcript"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="mt-1.5 flex items-start gap-1.5 min-w-0 rounded-md text-teal-700 dark:text-teal-300 hover:text-teal-900 dark:hover:text-teal-200 hover:underline"
            >
              <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5 opacity-80" aria-hidden />
              <span className="text-xs font-medium leading-snug line-clamp-2">
                {task.transcript?.title?.trim() || 'Open transcript'}
              </span>
            </Link>
          ) : null}

          {!transcriptHref && subscriberHref && contactLabel(task) ? (
            <Link
              to={subscriberHref}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline truncate block mt-1.5"
            >
              {contactLabel(task)}
            </Link>
          ) : null}

          {!transcriptHref && !subscriberHref && contactLabel(task) ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1.5">{contactLabel(task)}</p>
          ) : null}

          {transcriptHref && contactLabel(task) ? (
            subscriberHref ? (
              <Link
                to={subscriberHref}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 truncate block mt-1"
              >
                {contactLabel(task)}
              </Link>
            ) : (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-1">{contactLabel(task)}</p>
            )
          ) : null}

          <p
            className={`text-xs mt-1.5 ${
              overdue ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Due {formatDue(task.due_at)}
            {overdue ? ' · overdue' : ''}
          </p>
        </div>
      </div>
    </article>
  );
};

const TasksKanbanView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const epicFromUrl = searchParams.get('epic');
  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosedState] = useState(readStoredShowClosed);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [epicFilter, setEpicFilter] = useState<string>(() => epicFromUrl || readStoredEpic());
  const [searchQuery, setSearchQueryState] = useState(readStoredSearch);
  const [epics, setEpics] = useState<WorkEpicRow[]>([]);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [scoringTask, setScoringTask] = useState<NetworkingTaskRow | null>(null);
  const [scoreIntent, setScoreIntent] = useState<TaskScoreModalIntent>('review');
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<TaskKanbanColumnId | null>(null);
  const [boardMode, setBoardModeState] = useState<BoardMode>(() => readStoredBoardMode());
  const [timelineRange, setTimelineRangeState] = useState<TimelineRange>(readStoredTimelineRange);
  const timelineRef = useRef<TasksTimelineBoardHandle>(null);
  const busyRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);

  const setBoardMode = useCallback((mode: BoardMode) => {
    setBoardModeState(mode);
    writeStoredBoardMode(mode);
  }, []);

  const setTimelineRange = useCallback((next: TimelineRange) => {
    setTimelineRangeState(next);
    writeStoredTimelineRange(next);
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
    writeStoredSearch(query);
  }, []);

  const setShowClosed = useCallback((show: boolean | ((prev: boolean) => boolean)) => {
    setShowClosedState((prev) => {
      const next = typeof show === 'function' ? show(prev) : show;
      writeStoredShowClosed(next);
      return next;
    });
  }, []);

  const applyEpicFilter = useCallback(
    (next: string) => {
      setEpicFilter(next);
      writeStoredEpic(next);
      if (next === 'all') {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ epic: next }, { replace: true });
      }
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (epicFromUrl) {
      setEpicFilter(epicFromUrl);
      writeStoredEpic(epicFromUrl);
      return;
    }
    if (epicFilter !== 'all') {
      setSearchParams({ epic: epicFilter }, { replace: true });
    }
  }, [epicFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps -- URL wins on inbound links; restore stored epic into the address bar once.

  const fetchTasks = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      let query = supabase
        .from('tasks')
        .select(
          '*, subscriber:subscribers(id, name, email), transcript:transcripts(id, title, meeting_date), epic:work_epics(id, slug, title, target_date, status)',
        )
        .order('created_at', { ascending: false })
        .limit(1000);

      if (kindFilter === 'transcript') {
        query = query.eq('task_type', 'transcript_review');
      } else if (kindFilter === 'networking') {
        query = query.neq('task_type', 'transcript_review');
      }

      if (epicFilter !== 'all') {
        query = query.eq('epic_id', epicFilter);
      }

      if (!showClosed) {
        query = query.in('status', ['pending', 'in_progress']);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTasks((data ?? []) as NetworkingTaskRow[]);
    } catch (e: unknown) {
      if (!opts?.silent) {
        toast.error(e instanceof Error ? e.message : 'Failed to load tasks');
        setTasks([]);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [kindFilter, epicFilter, showClosed]);

  const fetchEpics = useCallback(async () => {
    const { data } = await supabase
      .from('work_epics')
      .select('*')
      .eq('status', 'active')
      .order('sort_order', { ascending: true });
    setEpics((data ?? []) as WorkEpicRow[]);
  }, []);

  useEffect(() => {
    void fetchEpics();
  }, [fetchEpics]);

  useEffect(() => {
    if (epicFilter === 'all' || epics.length === 0) return;
    if (!epics.some((e) => e.id === epicFilter)) {
      applyEpicFilter('all');
    }
  }, [epics, epicFilter, applyEpicFilter]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  busyRef.current = Boolean(scoringTask || intakeOpen || draggingId || updatingId);

  useEffect(() => {
    const scheduleRefresh = () => {
      if (busyRef.current) {
        pendingRefreshRef.current = true;
        return;
      }
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => {
        void fetchTasks({ silent: true });
        void fetchEpics();
      }, 250);
    };

    const channel = supabase
      .channel('eval-board-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'work_epics' }, scheduleRefresh)
      .subscribe();

    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      void supabase.removeChannel(channel);
    };
  }, [fetchTasks, fetchEpics]);

  useEffect(() => {
    if (scoringTask || intakeOpen || draggingId || updatingId || !pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    void fetchTasks({ silent: true });
    void fetchEpics();
  }, [scoringTask, intakeOpen, draggingId, updatingId, fetchTasks, fetchEpics]);

  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => {
      const hay = [
        t.title,
        t.notes,
        t.subscriber?.name,
        t.epic?.title,
        t.epic?.slug,
        t.transcript?.title,
        t.contact_email,
        t.strategy_score != null ? String(t.strategy_score) : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q) || q.split(/\s+/).every((token) => token && hay.includes(token));
    });
  }, [tasks, searchQuery]);

  const tasksByColumn = useMemo(() => {
    const grouped = new Map<TaskKanbanColumnId, NetworkingTaskRow[]>();
    for (const col of TASK_KANBAN_COLUMNS) {
      grouped.set(col.id, []);
    }
    for (const task of filteredTasks) {
      const colId = columnForTask(task);
      grouped.get(colId)?.push(task);
    }
    for (const col of TASK_KANBAN_COLUMNS) {
      grouped.get(col.id)?.sort(sortByValueThenDue);
    }
    return grouped;
  }, [filteredTasks]);

  const searchMatchCount = filteredTasks.length;

  const visibleColumns = useMemo(
    () =>
      showClosed
        ? TASK_KANBAN_COLUMNS
        : TASK_KANBAN_COLUMNS.filter(
            (c) => c.id === 'high_value' || c.id === 'needs_score' || c.id === 'low_pull',
          ),
    [showClosed],
  );

  const openTaskEval = (task: NetworkingTaskRow, intent: TaskScoreModalIntent = 'review') => {
    setIntakeOpen(false);
    setScoreIntent(intent);
    setScoringTask(task);
  };

  const setStatus = async (id: string, nextStatus: NetworkingTaskStatus) => {
    const task = tasks.find((t) => t.id === id);
    // Done is never one-click — capture value generated in chat first.
    if (nextStatus === 'done' && task) {
      openTaskEval(task, 'complete');
      if (!taskHasCompletionJustification(task)) {
        toast.message('Close-out needs value generated — what landed?');
      }
      return;
    }

    setUpdatingId(id);
    try {
      const { error } = await supabase
        .from('tasks')
        .update(taskStatusUpdatePatch(nextStatus))
        .eq('id', id);
      if (error) throw error;
      await fetchTasks();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const setDueAt = async (id: string, nextDueIso: string) => {
    const previous = tasks.find((t) => t.id === id)?.due_at;
    // Optimistic — keep scroll/layout stable; revert on failure
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, due_at: nextDueIso } : t)),
    );
    setUpdatingId(id);
    try {
      const { error } = await supabase.from('tasks').update({ due_at: nextDueIso }).eq('id', id);
      if (error) throw error;
      toast.success('Schedule updated');
    } catch (e: unknown) {
      if (previous != null) {
        setTasks((prev) =>
          prev.map((t) => (t.id === id ? { ...t, due_at: previous } : t)),
        );
      } else {
        await fetchTasks();
      }
      toast.error(e instanceof Error ? e.message : 'Could not reschedule');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDrop = async (columnId: TaskKanbanColumnId) => {
    if (!draggingId) return;
    const task = tasks.find((t) => t.id === draggingId);
    setDraggingId(null);
    setDropTarget(null);
    if (!task) return;

    const col = TASK_KANBAN_COLUMNS.find((c) => c.id === columnId);
    if (col?.dropMode === 'eval') {
      // Score buckets are views — open eval chat instead of inventing a score via drag.
      openTaskEval(task, 'review');
      if (columnId === 'high_value' && !isHighValueStrategyScore(task.strategy_score)) {
        toast.message('Raise value in chat to land in High value (score 4–5).');
      } else if (columnId === 'needs_score') {
        toast.message('Score this in chat before treating it as committed work.');
      } else if (columnId === 'low_pull') {
        toast.message('Low pull — raise value, justify in chat, or skip.');
      }
      return;
    }

    const nextStatus = kanbanDropStatus(columnId);
    if (!nextStatus || task.status === nextStatus) return;
    if (columnId === 'archived' && (task.status === 'skipped' || task.status === 'canceled')) return;
    if (nextStatus === 'done') {
      openTaskEval(task, 'complete');
      if (!taskHasCompletionJustification(task)) {
        toast.message('Close-out needs value generated — what landed?');
      }
      return;
    }

    await setStatus(task.id, nextStatus);
  };

  return (
    <div className="space-y-5 -mx-1 sm:-mx-0">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Kanban className="w-7 h-7 text-violet-500" />
            Eval board
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              setScoreIntent('create');
              setScoringTask(null);
              setIntakeOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Chat new task
          </button>
          <Link
            to="/tasks/epics"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Flag className="w-4 h-4" />
            Epics
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-800/80">
          <button
            type="button"
            onClick={() => setBoardMode('columns')}
            className={`inline-flex items-center justify-center p-1.5 rounded-md transition-colors ${
              boardMode === 'columns'
                ? 'bg-white dark:bg-gray-700 text-violet-700 dark:text-violet-200 shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Columns"
            aria-label="Columns"
          >
            <Kanban className="w-4 h-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setBoardMode('timeline')}
            className={`inline-flex items-center justify-center p-1.5 rounded-md transition-colors ${
              boardMode === 'timeline'
                ? 'bg-white dark:bg-gray-700 text-violet-700 dark:text-violet-200 shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Timeline"
            aria-label="Timeline"
          >
            <StretchHorizontal className="w-4 h-4" aria-hidden />
          </button>
        </div>
        {boardMode === 'timeline' ? (
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-800/80">
            <button
              type="button"
              onClick={() => setTimelineRange('week')}
              className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                timelineRange === 'week'
                  ? 'bg-white dark:bg-gray-700 text-violet-700 dark:text-violet-200 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Week
            </button>
            <button
              type="button"
              onClick={() => setTimelineRange('month')}
              className={`px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                timelineRange === 'month'
                  ? 'bg-white dark:bg-gray-700 text-violet-700 dark:text-violet-200 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Month
            </button>
          </div>
        ) : null}
        <label className="relative w-44 shrink-0">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tasks…"
            className="w-full pl-8 pr-8 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
            aria-label="Search tasks"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" aria-hidden />
            </button>
          ) : null}
        </label>
        {searchQuery.trim() ? (
          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {searchMatchCount} match{searchMatchCount === 1 ? '' : 'es'}
          </span>
        ) : null}
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as KindFilter)}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          title="Filter by task type"
          aria-label="Filter by task type"
        >
          <option value="all">All types</option>
          <option value="transcript">Transcripts</option>
          <option value="networking">Networking</option>
        </select>
        <select
          value={epicFilter}
          onChange={(e) => applyEpicFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          title="Filter by epic / plan"
        >
          <option value="all">All epics</option>
          {epics.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowClosed((v) => !v)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showClosed
              ? 'bg-gray-700 dark:bg-gray-500 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          {showClosed ? 'Hide done & archived' : 'Show done & archived'}
        </button>
        {boardMode === 'timeline' ? (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => timelineRef.current?.emailList()}
              className="inline-flex items-center justify-center p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Email list"
              aria-label="Email list"
            >
              <Mail className="w-4 h-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => timelineRef.current?.goToToday()}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Today
            </button>
          </div>
        ) : null}
      </div>

      {loading && tasks.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading board…
        </div>
      ) : boardMode === 'timeline' ? (
        <TasksTimelineBoard
          ref={timelineRef}
          range={timelineRange}
          tasks={filteredTasks.filter((t) =>
            showClosed ? true : t.status === 'pending' || t.status === 'in_progress',
          )}
          shareLabel={
            epicFilter !== 'all'
              ? epics.find((e) => e.id === epicFilter)?.title ?? 'Filtered epic'
              : 'All epics'
          }
          draggingId={draggingId}
          onDragStart={setDraggingId}
          onDragEnd={() => setDraggingId(null)}
          onOpenTask={(t) => openTaskEval(t, 'review')}
          onReschedule={setDueAt}
          updatingId={updatingId}
        />
      ) : (
        <div
          className={`grid gap-4 min-h-[calc(100vh-14rem)] ${
            visibleColumns.length === 3
              ? 'grid-cols-1 md:grid-cols-3'
              : visibleColumns.length === 2
                ? 'grid-cols-1 md:grid-cols-2'
                : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-5'
          }`}
        >
          {visibleColumns.map((column) => {
            const columnTasks = tasksByColumn.get(column.id) ?? [];
            const isDropTarget = dropTarget === column.id;

            return (
              <section
                key={column.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTarget(column.id);
                }}
                onDragLeave={() => {
                  if (dropTarget === column.id) setDropTarget(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  void handleDrop(column.id);
                }}
                className={`flex flex-col min-h-[280px] rounded-xl border-2 transition-colors ${column.accent} ${
                  isDropTarget ? 'border-dashed border-emerald-400 dark:border-emerald-500' : ''
                }`}
              >
                <header className={`px-3 py-2.5 rounded-t-[10px] border-b border-black/5 dark:border-white/5 ${column.header}`}>
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold">{column.label}</h2>
                    <span className="text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-white/60 dark:bg-black/20">
                      {columnTasks.length}
                    </span>
                  </div>
                  <p className="text-[11px] opacity-80 mt-0.5">{column.hint}</p>
                </header>

                <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-16rem)]">
                  {columnTasks.length === 0 ? (
                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 py-8 px-2">
                      {column.id === 'high_value'
                        ? 'Empty until chat scores something 4–5 — that’s your do-list.'
                        : column.id === 'needs_score'
                          ? 'Nothing unscored — every open task has a number.'
                          : column.id === 'low_pull'
                            ? 'No low-pull work. Raise, justify, or park elsewhere.'
                            : 'Empty'}
                    </p>
                  ) : (
                    columnTasks.map((task) => (
                      <TaskKanbanCard
                        key={task.id}
                        task={task}
                        onOpenChat={(t) => openTaskEval(t, 'review')}
                        onDragStart={setDraggingId}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDropTarget(null);
                        }}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <TaskScoreModal
        isOpen={scoringTask != null || intakeOpen}
        task={intakeOpen ? null : scoringTask}
        intent={intakeOpen ? 'create' : scoreIntent}
        defaultEpicId={epicFilter !== 'all' ? epicFilter : null}
        onClose={() => {
          setScoringTask(null);
          setScoreIntent('review');
          setIntakeOpen(false);
        }}
        onSaved={() => void fetchTasks({ silent: true })}
      />
    </div>
  );
};

export default TasksKanbanView;
